import { useMemo, useCallback } from 'react'
import {
  MDXEditor,
  headingsPlugin,
  linkPlugin,
  imagePlugin,
  listsPlugin,
  codeBlockPlugin,
  toolbarPlugin,
  BoldItalicUnderlineToggles,
  BlockTypeSelect,
  ListsToggle,
  InsertImage,
} from '@mdxeditor/editor'
import '@mdxeditor/editor/style.css'
import './MarkdownEditor.styles.less'
import { Col, Row, message } from 'antd'
import HttpRequest from '@/config/axios.config'

interface MarkdownEditorProps {
  markdown?: string
  onChanged?: (markdown: string) => void
  label?: string
  disabled?: boolean
}

interface MediaUploadResponse {
  data: {
    data: Array<{ url: string }>
  }
}

const MAX_IMAGE_SIZE = 5 * 1024 * 1024 // 5MB
const ALLOWED_IMAGE_TYPES = [
  'image/jpeg',
  'image/jpg',
  'image/png',
  'image/gif',
  'image/webp',
] as const

function MarkdownEditor({
  markdown = '',
  onChanged,
  label = 'Instructions',
  disabled = false,
}: MarkdownEditorProps) {
  const handleImageUpload = useCallback(
    async (image: File): Promise<string> => {
      try {
        // Validate file size
        if (image.size > MAX_IMAGE_SIZE) {
          const sizeMB = (MAX_IMAGE_SIZE / (1024 * 1024)).toFixed(0)
          message.error(`Image size should not exceed ${sizeMB}MB`)
          throw new Error('File size exceeds limit')
        }

        // Validate file type
        if (!ALLOWED_IMAGE_TYPES.includes(image.type as any)) {
          message.error('Only JPEG, PNG, GIF, and WebP images are allowed')
          throw new Error('Invalid file type')
        }

        const formData = new FormData()
        formData.append('files', image)

        const response = await HttpRequest.post(
          '/medias/multiple/documents',
          formData,
          {
            headers: {
              'Content-Type': 'multipart/form-data',
            },
          }
        )

        const imageUrl = response?.data?.data?.[0]?.url

        if (!imageUrl) {
          message.error('Failed to upload image')
          throw new Error('No URL returned from server')
        }

        message.success('Image uploaded successfully')
        return imageUrl
      } catch (error) {
        console.error('Image upload failed:', error)
        // Only show error message if we haven't already shown one
        if (
          error instanceof Error &&
          !error.message.includes('File size') &&
          !error.message.includes('Invalid file')
        ) {
          message.error('Failed to upload image. Please try again.')
        }
        throw error
      }
    },
    []
  )

  const editorPlugins = useMemo(
    () => [
      headingsPlugin(),
      linkPlugin(),
      imagePlugin({
        imageUploadHandler: handleImageUpload,
      }),
      listsPlugin(),
      codeBlockPlugin({ defaultCodeBlockLanguage: 'txt' }),
      toolbarPlugin({
        toolbarContents: () => (
          <>
            <BoldItalicUnderlineToggles />
            <ListsToggle options={['bullet', 'number']} />
            <InsertImage />
            <BlockTypeSelect />
          </>
        ),
      }),
    ],
    [handleImageUpload]
  )

  const handleChange = useCallback(
    (newMarkdown: string) => {
      onChanged?.(newMarkdown)
    },
    [onChanged]
  )

  return (
    <Row
      style={{ paddingBottom: 15 }}
      align="top"
      className="markdown-section-container"
    >
      <Col span={2} />
      <Col span={8}>
        <label className="label">{label}</label>
      </Col>
      <Col span={12}>
        <MDXEditor
          className="markdown"
          markdown={markdown}
          plugins={editorPlugins}
          onChange={handleChange}
          readOnly={disabled}
          contentEditableClassName="markdown-content"
        />
      </Col>
    </Row>
  )
}

export default MarkdownEditor
